import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function FleetPAttendance() {
    const API_URL = process.env.REACT_APP_API_URL;

    // Form state
    const [driverId, setDriverId] = useState('');
    const [actionType, setActionType] = useState('CHECK_IN'); // CHECK_IN or CHECK_OUT
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [checkInTime, setCheckInTime] = useState('');
    const [checkOutTime, setCheckOutTime] = useState('');
    const [kmReading, setKmReading] = useState('');
    const [vehiclePlate, setVehiclePlate] = useState('');
    const [notes, setNotes] = useState('');
    
    // Data state
    const [records, setRecords] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDrivers, setLoadingDrivers] = useState(false);
    const [loadingVehicles, setLoadingVehicles] = useState(false);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');

    // Mock vehicles fallback (pretend FleetP vehicles)
    const mockVehicles = [
        { plate: 'Vehicle A - DXB 1234', model: 'Toyota Hiace' },
        { plate: 'Vehicle B - DXB 5678', model: 'Nissan Urvan' },
        { plate: 'Vehicle C - AUH 9012', model: 'Toyota Coaster' },
        { plate: 'Vehicle D - SHJ 3456', model: 'Mitsubishi Rosa' },
        { plate: 'Vehicle E - AJM 7890', model: 'Hyundai H1' },
    ];

    useEffect(() => {
        fetchDrivers();
        fetchVehicles();
        fetchTodayRecords();
    }, []);

    const fetchDrivers = async () => {
        setLoadingDrivers(true);
        try {
            const token = sessionStorage.getItem('token');
            // Try FleetP API first
            const response = await axios.get(
                `${API_URL}shypvdriverapp/api/integrations/default/FLEETP/drivers`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data || [];
            if (Array.isArray(data) && data.length > 0) {
                setDrivers(data.map(d => ({
                    id: d.id,
                    name: d.name || `Driver ${d.id}`,
                    phone: d.phone || '',
                    license: d.license_no || '',
                })));
            } else {
                await fetchDriversFallback();
            }
        } catch (err) {
            console.error('Error fetching FleetP drivers, trying local:', err);
            await fetchDriversFallback();
        }
        setLoadingDrivers(false);
    };

    const fetchDriversFallback = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}shypvdriverapp/driver/getAllDrivers`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data || response.data?.responseObject || [];
            if (Array.isArray(data) && data.length > 0) {
                setDrivers(data.map(d => ({
                    id: d.driverId,
                    name: d.name || d.userName || `Driver ${d.driverId}`,
                })));
            }
        } catch (err) {
            console.error('Error fetching local drivers:', err);
        }
    };

    const fetchVehicles = async () => {
        setLoadingVehicles(true);
        try {
            const token = sessionStorage.getItem('token');
            // Try FleetP API first
            const response = await axios.get(
                `${API_URL}shypvdriverapp/api/integrations/default/FLEETP/vehicles`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data || [];
            if (Array.isArray(data) && data.length > 0) {
                setVehicles(data.map(v => ({
                    plate: v.plate_number || v.id,
                    model: v.model || 'Vehicle',
                })));
            } else {
                await fetchVehiclesFallback();
            }
        } catch (err) {
            console.error('Error fetching FleetP vehicles, trying local:', err);
            await fetchVehiclesFallback();
        }
        setLoadingVehicles(false);
    };

    const fetchVehiclesFallback = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}shypvdriverapp/vehicle/getVehicles`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data || response.data?.responseObject || [];
            if (Array.isArray(data) && data.length > 0) {
                setVehicles(data.map(v => ({
                    plate: v.vehicle_registration_number || v.vehicleRegistrationNumber || v.vehicle_name || `V-${v.vehicle_code}`,
                    model: v.vehicle_name || v.vehicleName || 'Vehicle',
                })));
            } else {
                setVehicles(mockVehicles);
            }
        } catch (err) {
            console.error('Error fetching local vehicles, using mock:', err);
            setVehicles(mockVehicles);
        }
    };

    const fetchTodayRecords = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const today = new Date().toISOString().split('T')[0];
            const response = await axios.get(
                `${API_URL}shypvdriverapp/driver-attendance/all?dateFrom=${today}&dateTo=${today}&size=50`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const pageData = response.data?.data || response.data?.responseObject;
            const data = pageData?.content || pageData || [];
            if (Array.isArray(data)) {
                setRecords(data.map(r => ({
                    id: r.id,
                    driverId: r.driverId,
                    driverName: r.driverName || `Driver ${r.driverId}`,
                    checkInTime: r.checkInTime ? new Date(r.checkInTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-',
                    checkOutTime: r.checkOutTime ? new Date(r.checkOutTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-',
                    vehiclePlate: r.vehiclePlateNumber || '-',
                    kmReading: r.kmReading || '-',
                    status: r.status || 'CHECKED_IN',
                    syncStatus: 'synced',
                })));
            }
        } catch (err) {
            console.error('Error fetching records:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!driverId) {
            setError('Please select a driver');
            return;
        }
        if (actionType === 'CHECK_IN' && !checkInTime) {
            setError('Please enter check-in time');
            return;
        }
        if (actionType === 'CHECK_OUT' && !checkOutTime) {
            setError('Please enter check-out time');
            return;
        }
        
        setLoading(true);
        setError('');
        setMsg('');
        
        try {
            const token = sessionStorage.getItem('token');
            const payload = {
                driverId: parseInt(driverId),
                vehiclePlateNumber: vehiclePlate || null,
                kmReading: kmReading ? parseFloat(kmReading) : null,
            };

            if (actionType === 'CHECK_IN' && checkInTime) {
                payload.checkInDateTime = `${date}T${checkInTime}:00`;
            }
            if (actionType === 'CHECK_OUT' && checkOutTime) {
                payload.checkOutDateTime = `${date}T${checkOutTime}:00`;
            }

            await axios.post(
                `${API_URL}shypvdriverapp/driver-attendance/manual`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setMsg(`${actionType === 'CHECK_IN' ? 'Check-in' : 'Check-out'} recorded successfully & synced to FleetP!`);
            
            // Reset form
            setDriverId('');
            setCheckInTime('');
            setCheckOutTime('');
            setKmReading('');
            setVehiclePlate('');
            setNotes('');
            
            // Refresh records
            fetchTodayRecords();
        } catch (err) {
            console.error('Error saving attendance:', err);
            setError(err.response?.data?.message || 'Failed to save attendance. Please try again.');
        }
        setLoading(false);
    };

    const getTodayRecords = () => {
        return records;
    };

    const getSyncStatusBadge = (status) => {
        if (status === 'synced') {
            return (
                <span style={styles.syncedBadge}>
                    <i className="fa fa-check" style={{ marginRight: '4px' }}></i>
                    Synced
                </span>
            );
        }
        return (
            <span style={styles.pendingBadge}>
                <i className="fa fa-clock-o" style={{ marginRight: '4px' }}></i>
                Pending
            </span>
        );
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>
                    <i className="fa fa-calendar-check-o" style={{ marginRight: '10px', color: '#1976d2' }}></i>
                    FleetP Attendance Record
                </h2>
                <p style={styles.subtitle}>
                    Record driver attendance with KM readings and vehicle information
                </p>
            </div>

            {/* Messages */}
            {msg && (
                <div style={styles.successAlert}>
                    <i className="fa fa-check-circle" style={{ marginRight: '8px' }}></i>
                    {msg}
                </div>
            )}
            {error && (
                <div style={styles.errorAlert}>
                    <i className="fa fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                    {error}
                </div>
            )}

            <div style={styles.gridContainer}>
                {/* Form Section */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>
                        <i className="fa fa-plus-circle" style={{ marginRight: '8px', color: '#28a745' }}></i>
                        Record Attendance
                    </h3>
                    
                    <form onSubmit={handleSubmit}>
                        {/* Action Type Toggle */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Action Type *</label>
                            <div style={styles.toggleRow}>
                                <button
                                    type="button"
                                    onClick={() => setActionType('CHECK_IN')}
                                    style={{
                                        ...styles.toggleBtn,
                                        backgroundColor: actionType === 'CHECK_IN' ? '#28a745' : '#f0f0f0',
                                        color: actionType === 'CHECK_IN' ? '#fff' : '#333',
                                    }}
                                >
                                    <i className="fa fa-sign-in" style={{ marginRight: '6px' }}></i>
                                    Check In
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActionType('CHECK_OUT')}
                                    style={{
                                        ...styles.toggleBtn,
                                        backgroundColor: actionType === 'CHECK_OUT' ? '#dc3545' : '#f0f0f0',
                                        color: actionType === 'CHECK_OUT' ? '#fff' : '#333',
                                    }}
                                >
                                    <i className="fa fa-sign-out" style={{ marginRight: '6px' }}></i>
                                    Check Out
                                </button>
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Driver *</label>
                            <select
                                value={driverId}
                                onChange={(e) => setDriverId(e.target.value)}
                                style={styles.select}
                                required
                            >
                                <option value="">{loadingDrivers ? 'Loading drivers...' : 'Select Driver'}</option>
                                {drivers.map(driver => (
                                    <option key={driver.id} value={driver.id}>
                                        {driver.name} (ID: {driver.id})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Vehicle Selection from FleetP */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                <i className="fa fa-truck" style={{ marginRight: '6px', color: '#1976d2' }}></i>
                                Select Vehicle (FleetP) *
                            </label>
                            <select
                                value={vehiclePlate}
                                onChange={(e) => setVehiclePlate(e.target.value)}
                                style={styles.select}
                                required
                            >
                                <option value="">{loadingVehicles ? 'Loading vehicles from FleetP...' : 'Select Vehicle'}</option>
                                {vehicles.map((v, idx) => (
                                    <option key={idx} value={v.plate}>
                                        {v.plate} — {v.model}
                                    </option>
                                ))}
                            </select>
                            <small style={{ color: '#888', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                <i className="fa fa-cloud" style={{ marginRight: '4px' }}></i>
                                Vehicles fetched from FleetP system
                            </small>
                        </div>

                        <div style={styles.formRow}>
                            <div style={styles.formGroupHalf}>
                                <label style={styles.label}>Date *</label>
                                <input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    style={styles.input}
                                    required
                                />
                            </div>
                        </div>

                        <div style={styles.formRow}>
                            {actionType === 'CHECK_IN' ? (
                                <div style={styles.formGroupHalf}>
                                    <label style={styles.label}>
                                        <i className="fa fa-sign-in" style={{ marginRight: '6px', color: '#28a745' }}></i>
                                        Check-in Time *
                                    </label>
                                    <input
                                        type="time"
                                        value={checkInTime}
                                        onChange={(e) => setCheckInTime(e.target.value)}
                                        style={styles.input}
                                        required
                                    />
                                </div>
                            ) : (
                                <div style={styles.formGroupHalf}>
                                    <label style={styles.label}>
                                        <i className="fa fa-sign-out" style={{ marginRight: '6px', color: '#dc3545' }}></i>
                                        Check-out Time *
                                    </label>
                                    <input
                                        type="time"
                                        value={checkOutTime}
                                        onChange={(e) => setCheckOutTime(e.target.value)}
                                        style={styles.input}
                                        required
                                    />
                                </div>
                            )}
                            <div style={styles.formGroupHalf}>
                                <label style={styles.label}>
                                    <i className="fa fa-tachometer" style={{ marginRight: '6px' }}></i>
                                    KM Reading
                                </label>
                                <input
                                    type="number"
                                    value={kmReading}
                                    onChange={(e) => setKmReading(e.target.value)}
                                    placeholder="Enter odometer reading"
                                    style={styles.input}
                                    min="0"
                                />
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Notes</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Any additional notes..."
                                style={styles.textarea}
                                rows={3}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                ...styles.submitButton,
                                backgroundColor: actionType === 'CHECK_IN' ? '#28a745' : '#dc3545',
                                opacity: loading ? 0.6 : 1
                            }}
                        >
                            {loading ? (
                                <>
                                    <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className={`fa fa-${actionType === 'CHECK_IN' ? 'sign-in' : 'sign-out'}`} style={{ marginRight: '8px' }}></i>
                                    {actionType === 'CHECK_IN' ? 'Record Check-In' : 'Record Check-Out'}
                                </>
                            )}
                        </button>

                        {/* FleetP Sync Notice */}
                        <div style={styles.syncNotice}>
                            <i className="fa fa-exchange" style={{ marginRight: '6px', color: '#1976d2' }}></i>
                            Data will be saved in system and synced to FleetP
                        </div>
                    </form>
                </div>

                {/* Today's Records Section */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>
                        <i className="fa fa-list" style={{ marginRight: '8px', color: '#17a2b8' }}></i>
                        Today's Attendance ({getTodayRecords().length})
                    </h3>
                    
                    {getTodayRecords().length === 0 ? (
                        <div style={styles.emptyState}>
                            <i className="fa fa-inbox" style={{ fontSize: '40px', color: '#ccc', marginBottom: '10px' }}></i>
                            <p>No attendance records for today</p>
                        </div>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Driver</th>
                                        <th style={styles.th}>Check-in</th>
                                        <th style={styles.th}>Check-out</th>
                                        <th style={styles.th}>Vehicle</th>
                                        <th style={styles.th}>KM</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Sync</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getTodayRecords().map(record => (
                                        <tr key={record.id}>
                                            <td style={styles.td}>
                                                <div style={styles.driverCell}>
                                                    <strong>{record.driverName}</strong>
                                                    <small style={{ color: '#666' }}>ID: {record.driverId}</small>
                                                </div>
                                            </td>
                                            <td style={styles.td}>{record.checkInTime || '-'}</td>
                                            <td style={styles.td}>{record.checkOutTime || '-'}</td>
                                            <td style={styles.td}>{record.vehiclePlate}</td>
                                            <td style={styles.td}>{record.kmReading}</td>
                                            <td style={styles.td}>
                                                <span style={{
                                                    ...styles.statusBadge,
                                                    backgroundColor: record.status === 'COMPLETED' ? '#d4edda' : '#fff3cd',
                                                    color: record.status === 'COMPLETED' ? '#155724' : '#856404',
                                                }}>
                                                    {record.status === 'COMPLETED' ? 'Completed' : 'Checked In'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{getSyncStatusBadge(record.syncStatus)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    header: {
        marginBottom: '24px',
    },
    title: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#1a1a2e',
        margin: '0 0 8px 0',
        display: 'flex',
        alignItems: 'center',
    },
    subtitle: {
        fontSize: '14px',
        color: '#666',
        margin: 0,
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
    },
    cardTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#333',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
    },
    formGroup: {
        marginBottom: '16px',
    },
    formRow: {
        display: 'flex',
        gap: '16px',
        marginBottom: '16px',
    },
    formGroupHalf: {
        flex: 1,
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
        marginBottom: '6px',
    },
    input: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        outline: 'none',
        boxSizing: 'border-box',
    },
    select: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        outline: 'none',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
    },
    textarea: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        outline: 'none',
        boxSizing: 'border-box',
        resize: 'vertical',
    },
    submitButton: {
        width: '100%',
        padding: '12px 20px',
        fontSize: '15px',
        fontWeight: '500',
        color: '#fff',
        backgroundColor: '#1976d2',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    toggleRow: {
        display: 'flex',
        gap: '8px',
    },
    toggleBtn: {
        flex: 1,
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: '500',
        border: '1px solid #ddd',
        borderRadius: '8px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
    },
    syncNotice: {
        marginTop: '12px',
        padding: '10px 12px',
        backgroundColor: '#e3f2fd',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#1565c0',
        textAlign: 'center',
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
    },
    successAlert: {
        padding: '12px 16px',
        backgroundColor: '#d4edda',
        color: '#155724',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
    },
    errorAlert: {
        padding: '12px 16px',
        backgroundColor: '#f8d7da',
        color: '#721c24',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
    },
    tableContainer: {
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        textAlign: 'left',
        padding: '12px 10px',
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        fontSize: '13px',
        fontWeight: '600',
        color: '#495057',
    },
    td: {
        padding: '12px 10px',
        borderBottom: '1px solid #dee2e6',
        fontSize: '14px',
        color: '#333',
    },
    driverCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    syncedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        backgroundColor: '#d4edda',
        color: '#155724',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
    },
    pendingBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        backgroundColor: '#fff3cd',
        color: '#856404',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px 20px',
        color: '#666',
    },
};
